import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { TenantContext } from "v3/context/tenant";
import { BraintreeContext } from "v3/context/braintree";
import { PaymentContext } from "v3/context/payment";
import { CheckoutContext } from "v3/context/checkout";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { EmptyCart } from "@pepdirect/v3/ui/EmptyCart";
import { CheckoutBreadcrumbs } from "v3/components/checkout/Breadcrumbs";
import { SummaryAndItems } from "v3/components/orderDetails/SummaryAndItems";
import { LoyaltyPageError } from "v3/components/checkout/LoyaltyPageError";
import { LoadingPage } from "v3/components/LoadingPage";
import { Custom404 } from "v3/components/Custom404";
import { CheckoutSteps } from "v3/components/checkout/Steps";
import {
  AllowedPaymentMethod,
  CheckoutSessionFragment,
  SessionStage,
  useAvailablePaymentMethodsLazyQuery,
  useShopToCheckoutMutation,
} from "v3/services/graphql/generated";
import {
  logAddShippingInfo,
  logOrderSummaryPageView,
} from "v3/services/analytics";
import { scrollTo } from "@pepdirect/v3/helpers/scroll";
import { CHECKOUT_STEPS } from "v3/types/checkout";
import { endpoints } from "v3/config";
import { StyledAlert, StyledEmptyCart, StyledSpinner } from "./index.styled";
import {
  StyledOuterContent,
  StyledInnerContent,
  StyledCheckoutMainContent,
  StyledCheckoutLeftColumn,
} from "v3/styles/checkout/pageLayout.styled";
import { OrderProcessing } from "v3/components/OrderProcessing";
import { LocalizationContext } from "v3/context/localization";
import { EMBEDDED_GIFT_CARD_TYPES } from "v3/types/payment";

const InternetConnectionMonitor = dynamic(
  () => import("v3/components/network/InternetConnectionMonitor"),
  { ssr: false }
);

interface CheckoutContainerProps {
  cartId: string;
}

export const CheckoutContainer: FC<CheckoutContainerProps> = ({ cartId }) => {
  const router = useRouter();

  const {
    cartErrors,
    errorMsg,
    handleCheckoutErrors,
    showPaymentProcessingPage,
    itemErrors,
    loyaltyError,
    setErrorMsg,
    show404Page,
    showEmptyCartPage,
    showLoyaltyErrorPage,
  } = useContext(CheckoutErrorContext);
  const { setAvailablePaymentMethods, setPaymentType } =
    useContext(PaymentContext);
  const { tenant, isStripeTenant } = useContext(TenantContext);
  const { localization } = useContext(LocalizationContext);
  const { client, setClient } = useContext(BraintreeContext);
  const {
    checkoutSession,
    isLoading,
    setCheckoutSession,
    setIsLoading,
    step,
    transitionToStep,
    setCartId,
  } = useContext(CheckoutContext);
  const [isSessionReady, setIsSessionReady] = useState(false);

  const [getAvailablePaymentMethods] = useAvailablePaymentMethodsLazyQuery();
  const [shopToCheckout] = useShopToCheckoutMutation();

  const fallbackUrl = tenant?.fallbackUrl || "";
  const {
    paymentFailureErrorText = "There was an error processing your payment. Please try again or choose a new payment method to continue.",
  } = localization?.checkout?.errors || {};

  const skipToReviewStep = useCallback(
    async (session: CheckoutSessionFragment) => {
      try {
        const { data, error } = await getAvailablePaymentMethods({
          variables: {
            id: session.id,
          },
        });
        if (error) throw error;

        if (data?.checkoutSession?.availablePaymentMethods) {
          setAvailablePaymentMethods(
            data.checkoutSession.availablePaymentMethods
          );
          setPaymentType(
            session.payment?.paymentMethod || AllowedPaymentMethod.CreditCard
          );
        }
        logAddShippingInfo(
          session.cart,
          session.shippingMethodConfig?.name.toLowerCase() || null
        );
        transitionToStep(CHECKOUT_STEPS.review);
      } catch (e) {
        handleCheckoutErrors(e);
      }
      setIsSessionReady(true);
    },
    [
      getAvailablePaymentMethods,
      handleCheckoutErrors,
      setAvailablePaymentMethods,
      setPaymentType,
      transitionToStep,
    ]
  );

  const logOrderSummaryPageViewEvent = useCallback(
    (checkoutSessionFragment: CheckoutSessionFragment) => {
      const { email, id: username } = checkoutSessionFragment.user || {};
      logOrderSummaryPageView(checkoutSessionFragment?.cart, username, email);
    },
    []
  );

  useEffect(() => {
    const processCheckoutSession = async () => {
      if (!checkoutSession) return;
      if (checkoutSession.stage === SessionStage.Review) {
        skipToReviewStep(checkoutSession);
      } else {
        setIsSessionReady(true);
        setIsLoading(false);
      }
    };
    if (!isSessionReady) processCheckoutSession();
  }, [
    checkoutSession,
    handleCheckoutErrors,
    isSessionReady,
    setIsLoading,
    skipToReviewStep,
  ]);

  /**
   * 👀 THIS RUNS FIRST UPON LANDING ON THE CHECKOUT PAGE 👀
   */
  useEffect(() => {
    const getCheckoutSession = async () => {
      setIsLoading(true);
      try {
        const { data } = await shopToCheckout({
          variables: {
            cartId,
          },
        });
        const shopToCheckoutData = data?.shopToCheckout;
        if (!shopToCheckoutData) return;
        const isP24Redirect = !!router.query.payment_intent;
        if (isP24Redirect && shopToCheckoutData.payment?.lastErrorCode) {
          setErrorMsg(paymentFailureErrorText);
        }
        logOrderSummaryPageViewEvent(shopToCheckoutData);
        setCheckoutSession(shopToCheckoutData);
      } catch (e) {
        handleCheckoutErrors(e);
        setIsSessionReady(true);
        setIsLoading(false);
      }
    };
    if (!isLoading && !isSessionReady) getCheckoutSession();
  }, [
    cartId,
    handleCheckoutErrors,
    isLoading,
    isSessionReady,
    isStripeTenant,
    logOrderSummaryPageViewEvent,
    paymentFailureErrorText,
    router.query.payment_intent,
    setCheckoutSession,
    setErrorMsg,
    setIsLoading,
    shopToCheckout,
  ]);

  useEffect(() => {
    setCartId(cartId);
  }, [cartId, setCartId]);

  // Remove P24 query params if any from redirect
  useEffect(() => {
    const { cartId, appId, payment_intent } = router.query;
    if (payment_intent)
      router.replace(`/v3/${cartId}/${appId}`, undefined, { shallow: true });
  }, [router]);

  // set braintree client
  useEffect(() => {
    if (!isStripeTenant && !client) setClient();
  }, [client, setClient, isStripeTenant]);

  useEffect(() => {
    if (cartErrors?.length || itemErrors?.length || errorMsg) {
      scrollTo();
    }
  }, [cartErrors, itemErrors, errorMsg]);

  if (!isSessionReady)
    return (
      <StyledSpinner>
        <LoadingPage />
      </StyledSpinner>
    );

  if (
    (checkoutSession && !checkoutSession.price.lineItems.length) ||
    showEmptyCartPage
  ) {
    return (
      <StyledEmptyCart>
        <EmptyCart cdnEndpoint={endpoints.cdn} href={fallbackUrl} />
      </StyledEmptyCart>
    );
  }

  if (showLoyaltyErrorPage) {
    return <LoyaltyPageError fallbackUrl={fallbackUrl} />;
  }

  if (showPaymentProcessingPage) {
    return <OrderProcessing />;
  }

  if (!checkoutSession || show404Page) {
    return <Custom404 fallbackUrl={fallbackUrl} />;
  }

  return (
    <>
      <InternetConnectionMonitor />
      <CheckoutBreadcrumbs
        currentStep={step}
        isLoading={isLoading}
        onClick={(newStep) => {
          transitionToStep(newStep);
        }}
      />
      <StyledOuterContent>
        <StyledInnerContent>
          <StyledCheckoutMainContent>
            <StyledCheckoutLeftColumn>
              {errorMsg && (
                <StyledAlert
                  type="error"
                  bold
                  closable
                  onClose={() => setErrorMsg("")}
                >
                  {errorMsg}
                </StyledAlert>
              )}
              {!!cartErrors.length &&
                cartErrors.map(({ alertType, msg }, i) => (
                  <StyledAlert type={alertType} bold key={`cart-error-${i}`}>
                    {msg}
                  </StyledAlert>
                ))}
              <CheckoutSteps />
            </StyledCheckoutLeftColumn>

            <SummaryAndItems
              itemErrors={itemErrors}
              tenant={tenant}
              loyaltyError={loyaltyError}
              price={checkoutSession.price}
              giftCard={checkoutSession.giftCards?.find(
                (item) => item?.type === EMBEDDED_GIFT_CARD_TYPES.BALANCE
              )}
            />
          </StyledCheckoutMainContent>
        </StyledInnerContent>
      </StyledOuterContent>
    </>
  );
};
